/**
 * Finance → Investors.
 *
 * Every investment the business has taken, kept apart by kind: vehicle
 * partnerships, equity and profit sharing. An investor may hold several, of
 * different kinds; each is its own row with its own terms, scope and money.
 *
 * The cards are one per kind AND currency. Money in two currencies is never
 * added together — there is no conversion here to add it with.
 */

import { useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Building2, Eye, HandCoins, Landmark, Pencil, Plus, Search, ShieldCheck } from 'lucide-react';
import {
  COUNTRIES,
  INVESTMENT_SCOPE_KINDS,
  INVESTMENT_SCOPE_LABELS,
  INVESTMENT_STATUSES,
  INVESTMENT_STATUS_LABELS,
  INVESTMENT_TYPE_EXPLANATIONS,
  INVESTMENT_TYPE_LABELS,
  statesOf,
  type InvestmentListQuery,
  type InvestmentStatus,
  type InvestmentSummaryCard,
  type InvestmentType,
  type InvestmentView,
} from '@cenox/shared';
import { ActionMenu, Badge, Button, Card, EmptyState, ErrorState, Input, MenuItem, Select, TableSkeleton, cn } from '../../components/ui';
import { useAuth } from '../../lib/auth';
import { money } from '../../lib/format';
import { InvestmentPanel } from './InvestmentPanel';
import { AccessDialog, DividendDialog, EntitiesDialog } from './InvestmentDialogs';
import { useInvestment, useInvestments, useInvestorProfiles } from './investmentData';

const TABS: Array<{ key: 'all' | InvestmentType; label: string }> = [
  { key: 'all', label: 'All investments' },
  { key: 'vehicle_partnership', label: 'Vehicle partners' },
  { key: 'equity', label: 'Equity' },
  { key: 'profit_sharing', label: 'Profit sharing' },
];

const STATUS_TONES: Record<InvestmentStatus, 'neutral' | 'success' | 'warning' | 'danger' | 'info'> = {
  draft: 'neutral',
  active: 'success',
  suspended: 'warning',
  ended: 'info',
};

export function StatusBadge({ status }: { status: InvestmentStatus }): ReactNode {
  return <Badge tone={STATUS_TONES[status]}>{INVESTMENT_STATUS_LABELS[status]}</Badge>;
}

export function TypeBadge({ type }: { type: InvestmentType }): ReactNode {
  return (
    <span className="inline-flex items-center rounded-full bg-violet-50 px-2 py-0.5 text-xs font-medium text-violet-700 ring-1 ring-inset ring-violet-200">
      {INVESTMENT_TYPE_LABELS[type]}
    </span>
  );
}

/** What the row says it holds: its vehicles, or its percentage. */
export function holdingText(i: InvestmentView): string {
  if (i.type === 'vehicle_partnership') {
    const current = i.vehicles.filter((v) => v.current);
    if (current.length === 0) return i.vehicles.length ? 'No vehicle now' : 'No vehicle yet';
    return current.map((v) => v.registration).join(', ');
  }
  if (i.type === 'equity') return i.ownershipPercent ? `${i.ownershipPercent}% of ${i.entity?.name ?? 'the entity'}` : 'Percentage not set';
  return i.profitPercent ? `${i.profitPercent}% of profit` : 'Percentage not set';
}

function SummaryCard({ card }: { card: InvestmentSummaryCard }): ReactNode {
  const currency = card.currencyCode ?? '';
  const earnedLabel = card.type === 'vehicle_partnership' ? 'Collected for investors' : 'Allocated';
  return (
    <div className="rounded-xl border border-violet-100 bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-medium text-violet-700">{INVESTMENT_TYPE_LABELS[card.type]}</p>
        <span className="rounded bg-violet-50 px-1.5 py-0.5 text-xs font-semibold text-violet-700">{card.currencyCode ?? 'No currency'}</span>
      </div>
      <p className="mt-1 text-xs text-slate-500">
        {card.count} investment{card.count === 1 ? '' : 's'} · {card.active} active
      </p>
      <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1.5 text-sm">
        <dt className="text-slate-500">Invested</dt>
        <dd className="text-right tabular-nums text-slate-900">{currency ? money(card.invested, currency) : '—'}</dd>
        <dt className="text-slate-500">{earnedLabel}</dt>
        <dd className="text-right tabular-nums text-slate-900">{currency ? money(card.earned, currency) : '—'}</dd>
        <dt className="text-slate-500">Paid</dt>
        <dd className="text-right tabular-nums text-slate-900">{currency ? money(card.paid, currency) : '—'}</dd>
        <dt className="font-medium text-slate-700">Outstanding</dt>
        <dd className="text-right font-semibold tabular-nums text-slate-900">{currency ? money(card.outstanding, currency) : '—'}</dd>
      </dl>
    </div>
  );
}

export function InvestmentsPage(): ReactNode {
  const { business, can } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = (TABS.find((t) => t.key === params.get('tab'))?.key ?? 'all') as (typeof TABS)[number]['key'];
  const mayManage = can('investments:manage');
  const mayPost = can('investments:post');
  const isOwner = business?.role === 'owner';

  const [search, setSearch] = useState('');
  const [investorId, setInvestorId] = useState('');
  const [status, setStatus] = useState<InvestmentStatus | ''>('');
  const [scopeKind, setScopeKind] = useState('');
  const [country, setCountry] = useState('');
  const [state, setState] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [dialog, setDialog] = useState<'dividend' | 'entities' | 'access' | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  const query: InvestmentListQuery = useMemo(
    () => ({
      ...(tab !== 'all' ? { type: tab } : {}),
      ...(investorId ? { investorId } : {}),
      ...(status ? { status } : {}),
      ...(scopeKind ? { scopeKind: scopeKind as InvestmentListQuery['scopeKind'] } : {}),
      ...(country ? { country } : {}),
      ...(country && state ? { state } : {}),
      ...(from ? { from } : {}),
      ...(to ? { to } : {}),
      ...(search.trim() ? { search: search.trim() } : {}),
    }),
    [tab, investorId, status, scopeKind, country, state, from, to, search],
  );
  const list = useInvestments(query);
  const investors = useInvestorProfiles();
  const editing = useInvestment(editingId);

  if (!can('investments:read')) {
    return (
      <EmptyState
        icon={<HandCoins className="size-8" />}
        title="Investors are for the owner and those the owner allows"
        description="Ask the owner for access to investments."
      />
    );
  }

  const rows = list.data?.rows ?? [];
  const cards = list.data?.summary ?? [];
  /*
    Investors who hold no investment yet — added before investments existed,
    or whose only one was removed. Only read off the unfiltered list, where
    every investment is in view: under a filter, "not listed" means nothing.
  */
  const unfiltered = Object.keys(query).length === 0;
  const holding = new Set(rows.map((r) => r.investorId));
  const without = unfiltered && list.data ? (investors.data ?? []).filter((p) => !holding.has(p.id)) : [];

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Investors</h1>
          <p className="mt-0.5 text-sm text-slate-500">Vehicle partnerships, equity and profit sharing — each investment kept apart.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {mayPost ? (
            <Button variant="secondary" size="sm" icon={<Landmark className="size-4" />} onClick={() => setDialog('dividend')}>
              Declare dividend
            </Button>
          ) : null}
          <Button variant="secondary" size="sm" icon={<Building2 className="size-4" />} onClick={() => setDialog('entities')}>
            Entities
          </Button>
          {isOwner ? (
            <Button variant="secondary" size="sm" icon={<ShieldCheck className="size-4" />} onClick={() => setDialog('access')}>
              Access
            </Button>
          ) : null}
          {mayManage ? (
            <Button size="sm" icon={<Plus className="size-4" />} className="bg-violet-600 hover:bg-violet-700" onClick={() => setAdding(true)}>
              Add investment
            </Button>
          ) : null}
        </div>
      </header>

      <nav className="flex gap-1 overflow-x-auto border-b border-slate-200 pb-px" aria-label="Investment kinds">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            aria-current={tab === t.key ? 'page' : undefined}
            onClick={() => setParams(t.key === 'all' ? {} : { tab: t.key })}
            className={cn(
              'whitespace-nowrap rounded-t-lg border-b-2 px-3.5 py-2 text-sm font-medium transition-colors',
              tab === t.key ? 'border-violet-600 text-violet-700' : 'border-transparent text-slate-500 hover:text-slate-800',
            )}
          >
            {t.label}
          </button>
        ))}
      </nav>
      {tab !== 'all' ? <p className="text-sm text-slate-600">{INVESTMENT_TYPE_EXPLANATIONS[tab]}</p> : null}

      {saved ? (
        <p className="rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-800" role="status">
          {saved}
        </p>
      ) : null}

      {cards.length ? (
        <section aria-label="Summary by kind and currency" className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {cards.map((c) => (
            <SummaryCard key={`${c.type}:${c.currencyCode}`} card={c} />
          ))}
        </section>
      ) : null}
      {list.data?.incomplete ? (
        <p className="text-xs text-slate-500">
          {list.data.incomplete} investment{list.data.incomplete === 1 ? ' has' : 's have'} no amount or currency recorded: counted, not added to Invested.
        </p>
      ) : null}

      <Card className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Input
            label="Search"
            type="search"
            prefix=""
            placeholder="Investor, reference, vehicle, entity"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Select
            label="Investor"
            value={investorId}
            placeholder="All investors"
            options={(investors.data ?? []).map((i) => ({ value: i.id, label: i.name }))}
            onChange={(e) => setInvestorId(e.target.value)}
          />
          <Select
            label="Status"
            value={status}
            placeholder="Any status"
            options={INVESTMENT_STATUSES.map((s) => ({ value: s, label: INVESTMENT_STATUS_LABELS[s] }))}
            onChange={(e) => setStatus(e.target.value as InvestmentStatus | '')}
          />
          <Select
            label="Scope"
            value={scopeKind}
            placeholder="Any scope"
            options={INVESTMENT_SCOPE_KINDS.map((k) => ({ value: k, label: INVESTMENT_SCOPE_LABELS[k] }))}
            onChange={(e) => setScopeKind(e.target.value)}
          />
          <Select
            label="Applies in country"
            value={country}
            placeholder="Anywhere"
            options={COUNTRIES.map((c) => ({ value: c.code, label: c.name }))}
            onChange={(e) => {
              setCountry(e.target.value);
              setState('');
            }}
          />
          <Select
            label="…and state"
            value={state}
            placeholder="Any"
            disabled={!country || statesOf(country).length === 0}
            options={statesOf(country).map((s) => ({ value: s.code, label: s.name }))}
            onChange={(e) => setState(e.target.value)}
          />
          <Input label="In force from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          <Input label="to" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
      </Card>

      {list.isError ? (
        <ErrorState message="Could not load the investments." onRetry={() => void list.refetch()} />
      ) : list.isPending ? (
        <TableSkeleton rows={4} columns={6} />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<Search className="size-8" />}
          title={Object.keys(query).length > (tab === 'all' ? 0 : 1) ? 'No investment matches' : 'No investments yet'}
          description={
            mayManage
              ? 'Add an investment: a vehicle partnership, equity in an entity, or a share of defined profit.'
              : 'Nothing to show here yet.'
          }
          {...(mayManage ? { action: <Button onClick={() => setAdding(true)}>Add investment</Button> } : {})}
        />
      ) : (
        <Card padded={false}>
          {/* Phone: one card per investment. */}
          <ul className="divide-y divide-slate-100 sm:hidden" aria-label="Investments">
            {rows.map((i) => (
              <li key={i.id} className="space-y-1.5 p-4">
                <div className="flex items-start justify-between gap-2">
                  <Link to={`/finance/investors/${i.investorId}`} className="font-medium text-slate-900 hover:text-violet-700">
                    {i.investorName}
                  </Link>
                  <StatusBadge status={i.status} />
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                  <TypeBadge type={i.type} />
                  <span className="text-xs text-slate-500">{i.scope.label}</span>
                </div>
                <p className="text-sm text-slate-600">{holdingText(i)}</p>
                <div className="flex gap-2 pt-1">
                  <Button size="sm" variant="secondary" icon={<Eye className="size-4" />} onClick={() => navigate(`/finance/investors/${i.investorId}#${i.id}`)}>
                    View
                  </Button>
                  {mayManage ? (
                    <Button size="sm" variant="secondary" icon={<Pencil className="size-4" />} onClick={() => setEditingId(i.id)}>
                      Edit
                    </Button>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
          {/* Wider: a table. */}
          <div className="hidden overflow-x-auto sm:block">
            <table className="w-full min-w-[760px] text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="px-4 py-2.5 font-medium">Investor</th>
                  <th className="px-3 py-2.5 font-medium">Investment type</th>
                  <th className="px-3 py-2.5 font-medium">Scope</th>
                  <th className="px-3 py-2.5 font-medium">Vehicles or percentage</th>
                  <th className="px-3 py-2.5 font-medium">Status</th>
                  <th className="sticky right-0 bg-white px-3 py-2.5 shadow-[-6px_0_6px_-6px_rgba(15,23,42,0.15)]">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((i) => (
                  <tr key={i.id} className="hover:bg-violet-50/40">
                    <td className="px-4 py-2.5">
                      <Link to={`/finance/investors/${i.investorId}`} className="font-medium text-slate-900 hover:text-violet-700">
                        {i.investorName}
                      </Link>
                      {i.missing.length ? (
                        <span className="block text-xs text-amber-700">To complete: {i.missing.slice(0, 3).join(', ')}{i.missing.length > 3 ? '…' : ''}</span>
                      ) : i.reference ? (
                        <span className="block text-xs text-slate-400">{i.reference}</span>
                      ) : null}
                    </td>
                    <td className="px-3 py-2.5">
                      <TypeBadge type={i.type} />
                    </td>
                    <td className="px-3 py-2.5 text-slate-600">{i.scope.label}</td>
                    <td className="px-3 py-2.5 text-slate-700">{holdingText(i)}</td>
                    <td className="px-3 py-2.5">
                      <StatusBadge status={i.status} />
                    </td>
                    <td className="sticky right-0 bg-white px-3 py-1.5 text-right shadow-[-6px_0_6px_-6px_rgba(15,23,42,0.15)]">
                      <ActionMenu label={`Actions for ${i.investorName}'s ${INVESTMENT_TYPE_LABELS[i.type].toLowerCase()}`}>
                        <MenuItem icon={<Eye className="size-4" />} onSelect={() => navigate(`/finance/investors/${i.investorId}#${i.id}`)}>
                          View
                        </MenuItem>
                        {mayManage ? (
                          <MenuItem icon={<Pencil className="size-4" />} onSelect={() => setEditingId(i.id)}>
                            Edit
                          </MenuItem>
                        ) : null}
                      </ActionMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {without.length ? (
        <section aria-label="Investors without an investment" className="space-y-2">
          <h2 className="text-sm font-semibold text-slate-900">Investors without an investment ({without.length})</h2>
          <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white text-sm">
            {without.map((p) => (
              <li key={p.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
                <Link to={`/finance/investors/${p.id}`} className="font-medium text-slate-900 hover:text-violet-700">
                  {p.name}
                </Link>
                <span className="text-xs text-slate-500">
                  {p.reference ?? 'No investor ID'}
                  {p.isActive ? '' : ' · inactive'}
                </span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {adding ? (
        <InvestmentPanel
          {...(tab !== 'all' ? { type: tab } : {})}
          onClose={() => setAdding(false)}
          onSaved={(m) => {
            setAdding(false);
            setSaved(m);
          }}
        />
      ) : null}
      {editingId && editing.data ? (
        <InvestmentPanel
          investment={editing.data}
          onClose={() => setEditingId(null)}
          onSaved={(m) => {
            setEditingId(null);
            setSaved(m);
          }}
        />
      ) : null}
      {dialog === 'dividend' ? <DividendDialog onClose={() => setDialog(null)} onDone={(m) => { setDialog(null); setSaved(m); }} /> : null}
      {dialog === 'entities' ? <EntitiesDialog mayManage={mayManage} onClose={() => setDialog(null)} /> : null}
      {dialog === 'access' ? <AccessDialog onClose={() => setDialog(null)} /> : null}
    </div>
  );
}
